import {
  HL7SettingsDto,
  QueryFactory,
  TransportProtocol,
} from '@lmt/shared-types';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';

import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import {
  hl7DefaultSettings,
  HL7OutgoingSettingsData,
  HL7SettingsData,
} from './types';
import { hl7SettingsDataYupSchema } from './validation';
import { HL7SettingsBlock } from './HL7SettingsBlock';
import { HL7OutgoingSettingsBlock } from './HL7OutgoingSettingsBlock';
import { HL7IncomingSettingsBlock } from './HL7IncomingSettingsBlock';

import styles from './../SettingsBlock.module.scss';
import { TransporterState } from '../../../gdt/messages/components/TransporterState';
import { VisitCreatingSettings } from './VisitCreatingSettings';
import { TenantSettingsKeys } from '../types';
import { useRegisterUnsavedChanges } from '../useRegisterUnsavedChanges';

export const Hl7Settings: FC = React.memo(function Hl7Settings() {
  const i18next = useScopedTranslation('tenant_settings.hl7');
  const queryClient = useQueryClient();
  const modals = useModal();

  const hl7SettingsQuery = QueryFactory.HL7SettingsQuery.useGetQuery();
  const tenantSettingsQuery = QueryFactory.TenantSettingsQuery.useGetQuery();
  const isServerEnabled = !(
    tenantSettingsQuery.data?.allowTenantCreation ?? true
  );

  const hl7OutgoingSettingsQuery =
    QueryFactory.HL7OutgoingSettingsQuery.useGetQuery();

  const hl7IncomingSettingsQuery =
    QueryFactory.HL7IncomingSettingsQuery.useGetQuery();

  const hl7TemplateQuery = QueryFactory.HL7OutgoingTemplatesQuery.useGetQuery();

  const settingsData = useMemo(() => {
    const generalSettings = hl7SettingsQuery.data as HL7SettingsDto;
    const outgoingSettings =
      hl7OutgoingSettingsQuery.data as HL7OutgoingSettingsData;
    const data: HL7SettingsData = {
      ...hl7DefaultSettings,
      ...generalSettings,
      ...outgoingSettings,
      ...hl7IncomingSettingsQuery.data,
      encoding: hl7SettingsQuery.data?.encoding ?? '',
      visitLengthInHours: hl7IncomingSettingsQuery.data
        ? hl7IncomingSettingsQuery.data.visitLengthInSeconds / 3600
        : hl7DefaultSettings.visitLengthInHours,
      additionalVisits: hl7IncomingSettingsQuery.data?.additionalVisits ?? [],
      isHl7OutgoingEnabled: hl7OutgoingSettingsQuery.data?.enabled ?? false,
    };
    return data;
  }, [
    hl7SettingsQuery.data,
    hl7OutgoingSettingsQuery.data,
    hl7IncomingSettingsQuery.data,
    hl7TemplateQuery.data,
  ]);

  const form = useAdvancedForm<HL7SettingsData>(
    useCallback(
      async (data) => {
        try {
          await QueryFactory.HL7SettingsClient.patch({
            encoding: data.encoding,
            facility: data.facility,
            appName: data.appName,
            onlyProcessMessagesForExistingPatients:
              data.onlyProcessMessagesForExistingPatients,
            subtenantId: data.subtenantId,
            ackProcessing: data.ackProcessing,
            cleanupEnabled: data.cleanupEnabled,
            deleteAfterMonths: data.deleteAfterMonths,
          });

          await QueryFactory.HL7OutgoingSettingsClient.patch({
            enabled: data.isHl7OutgoingEnabled,
            serverAddress: data.serverAddress,
            serverPort: data.serverPort,
            isSsl: data.isSsl,
            retryDelays: data.retryDelays,
            outgoingTransporterEnabled: data.outgoingTransporterEnabled,
            filesDirectory: data.filesDirectory,
          });

          await QueryFactory.HL7IncomingSettingsClient.patch({
            incomingTransporterEnabled: data.incomingTransporterEnabled,
            defaultVisitTypeId: data.defaultVisitTypeId,
            visitLengthInSeconds: Math.round(
              data.visitLengthInHours * 100 * 36,
            ), // round the duration in hours to two decimal places and convert to seconds
            visitStartTimeInSecondsSinceMidnight:
              data.visitStartTimeInSecondsSinceMidnight,
            visitSearchBackwardDaysCount: data.visitSearchBackwardDaysCount,
            visitSearchForwardDaysCount: data.visitSearchForwardDaysCount,
            caseSearchBackwardDaysCount: data.caseSearchBackwardDaysCount,
            caseSearchForwardDaysCount: data.caseSearchForwardDaysCount,
            overwriteTime: data.overwriteTime,
            overwriteTimeOption: data.overwriteTimeOption,
            additionalVisits: data.additionalVisits.map((v) => v),
          });

          await queryClient.invalidateQueries({
            queryKey: QueryFactory.HL7SettingsQuery.getQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.HL7OutgoingSettingsQuery.getQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.HL7IncomingSettingsQuery.getQueryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.HL7OutgoingTemplatesQuery.getQueryKey(),
          });
        } catch (e) {
          const errorDetails = handleSubmitFormError(e, setError);
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [hl7TemplateQuery.data],
    ),
    settingsData,
    {
      resolver: yupResolver(hl7SettingsDataYupSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      shouldUnregister: false,
      defaultValues: hl7DefaultSettings,
    },
  );

  const { setError, formState, reset } = form;

  const onReset = useCallback(() => {
    reset(settingsData);
  }, [settingsData]);

  useEffect(() => {
    onReset();
  }, [settingsData]);

  const isUnsavedChanges = useRegisterUnsavedChanges(
    TenantSettingsKeys.HL7,
    form,
  );

  return (
    <Loading
      loading={
        hl7SettingsQuery.isLoading ||
        hl7OutgoingSettingsQuery.isLoading ||
        hl7TemplateQuery.isLoading
      }
    >
      <form
        onSubmit={form.handleSubmitDefault}
        data-test-id="hl7-settings-form"
      >
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>{i18next.t('title')}</div>
              {(hl7OutgoingSettingsQuery.data?.outgoingTransporterEnabled ||
                hl7IncomingSettingsQuery.data?.incomingTransporterEnabled) && (
                <TransporterState protocol={TransportProtocol.HL7} />
              )}
            </div>
            <div className={styles.optionContainer}>
              <Button
                disabled={!isUnsavedChanges}
                title={i18next.t('reset_button_title')}
                className={styles.option}
                color={ButtonColor.Primary}
                onClick={onReset}
              />
              <SubmitButton
                disabled={!isUnsavedChanges}
                submitting={formState.isSubmitting}
                className={styles.option}
                title={i18next.t('submit_button_title')}
              />
            </div>
          </div>
          <div className={styles.dataContainer}>
            <HL7SettingsBlock
              form={form}
              isCleanupJobGloballyEnabled={
                hl7SettingsQuery.data?.isCleanupJobEnabledGlobally
              }
            />
            <HL7IncomingSettingsBlock
              form={form}
              isServerEnabled={isServerEnabled}
            />
            <HL7OutgoingSettingsBlock
              form={form}
              isServerEnabled={isServerEnabled}
            />
          </div>
          <div className={styles.dataContainer}>
            <VisitCreatingSettings form={form} />
          </div>
        </div>
      </form>
    </Loading>
  );
});
